import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { PageHeader } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";

export default function ElencoAtleti() {
  const [athletes, setAthletes] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/athletes").then((r) => {
      setAthletes(r.data);
      setLoading(false);
    });
  }, []);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return athletes;
    return athletes.filter(
      (a) =>
        a.cognome.toLowerCase().includes(q) ||
        a.nome.toLowerCase().includes(q) ||
        (a.societa || "").toLowerCase().includes(q)
    );
  }, [athletes, query]);

  return (
    <div data-testid="elenco-atleti-page">
      <PageHeader title="Elenco Atleti" subtitle={`${athletes.length} tesserati · Clicca su un atleta per la scheda anagrafica`}>
        <Badge className="bg-rugby-yellow text-black font-head uppercase tracking-wide hover:bg-rugby-yellow">
          {athletes.filter((a) => a.tesserato === "SI").length} Tesserati
        </Badge>
      </PageHeader>

      <div className="relative mb-6 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          data-testid="atleti-search-input"
          placeholder="Cerca per cognome, nome o società..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="pl-10 bg-black/50 border-border focus-visible:ring-rugby-yellow"
        />
      </div>

      <div className="border border-border bg-card overflow-hidden">
        {/* Header */}
        <div className="hidden md:grid grid-cols-[1.5fr_1.5fr_0.8fr_1fr_0.8fr_40px] gap-4 px-5 py-3 border-b border-border bg-[#0b0b0b] text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
          <div>Cognome</div>
          <div>Nome</div>
          <div>Anno</div>
          <div>Società</div>
          <div>Tesserato</div>
          <div></div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-muted-foreground">Caricamento...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">Nessun atleta trovato</div>
        ) : (
          filtered.map((a, i) => (
            <motion.div
              key={a.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: Math.min(i * 0.015, 0.4) }}
              data-testid={`atleta-row-${a.id}`}
              onClick={() => navigate(`/atleti/${a.id}`)}
              className="grid grid-cols-2 md:grid-cols-[1.5fr_1.5fr_0.8fr_1fr_0.8fr_40px] gap-2 md:gap-4 px-5 py-4 border-b border-border last:border-b-0 cursor-pointer hover:bg-white/5 transition-colors items-center group"
            >
              <div className="font-head font-semibold uppercase tracking-wide text-white group-hover:text-rugby-yellow transition-colors">
                {a.cognome}
              </div>
              <div className="font-body text-white/90">{a.nome}</div>
              <div className="font-mono text-sm text-muted-foreground">{a.anno_nascita || "—"}</div>
              <div className="text-sm text-muted-foreground">{a.societa || "—"}</div>
              <div>
                {a.tesserato === "SI" ? (
                  <span className="text-xs font-bold uppercase text-emerald-400">Sì</span>
                ) : (
                  <span className="text-xs font-bold uppercase text-rugby-red">No</span>
                )}
              </div>
              <ChevronRight className="hidden md:block h-4 w-4 text-muted-foreground group-hover:text-rugby-yellow transition-colors" />
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
}
